import React, { useEffect, useMemo } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { clearLoginError, loginUser } from '../../../store/reducers/authSlice';
import logo from "../../../assets/media/logo.png";
import GamingBoardBahamasLogo from "../../../assets/media/GamingBoardBahamasLogo.png";
import iconHide from "../../../assets/media/icon-hide.png";
import GBInput from '../../input';

const formatLockedUntil = (value) => {
    if (!value) return null;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    return date.toLocaleString(undefined, {
        year: 'numeric',
        month: 'short',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
    });
};

const Login = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const user = useSelector((state) => state.auth.user);
    const loginError = useSelector((state) => state.auth.loginError);
    const lockedUntil = useSelector((state) => state.auth.lockedUntil);
    const loading = useSelector((state) => state.auth.loading);

    const lockedUntilLabel = useMemo(
        () => formatLockedUntil(lockedUntil),
        [lockedUntil]
    );

    const isAccountLocked = Boolean(
        lockedUntil ||
        (loginError && /lock/i.test(loginError))
    );

    const {
        handleChange,
        handleBlur,
        handleSubmit,
        values,
        touched,
        errors,
    } = useFormik({
        initialValues: {
            email: '',
            password: '',
        },
        validationSchema: Yup.object({
            email: Yup.string().email('Invalid email address').required('Email is required'),
            password: Yup.string().required('Password is required'),
        }),
        onSubmit: (formValues) => {
            dispatch(loginUser({
                emailOrPhone: formValues.email?.toLowerCase()?.trim(),
                password: formValues.password,
            }));
        },
    });

    useEffect(() => {
        if (user?.access_token) {
            navigate('/dashboard');
        }
    }, [navigate, user]);

    const onFieldChange = (event) => {
        if (loginError) {
            dispatch(clearLoginError());
        }
        handleChange(event);
    };

    const renderInputField = (name, label, type, iconSrc, placeholder) => (
        <div className={`fieldWrap ${touched[name] && errors[name] ? 'error-message' : ''}`}>
            <GBInput
                label={label}
                type={type}
                iconSrc={type !== 'password' && iconSrc}
                placeholder={placeholder}
                id={name}
                name={name}
                setValue={onFieldChange}
                onBlur={handleBlur}
                value={values[name]}
            />
            {touched[name] && errors[name] && <div className="error-message">{errors[name]}</div>}
        </div>
    );

    return (
        <div className="App">
            <div className="container">
                <div className='landingWrapper'>
                    <div className='landingRow'>
                        <div className='landingCol'>
                            <div className='fieldsWrapper'>
                                <img src={logo} className="logo" alt="logo" />
                                <h2>Sign in</h2>
                                <p>Welcome to the Gaming Board of The Bahamas Digital Application Portal.</p>
                                {loginError && (
                                    <div
                                        className="login-api-error"
                                        role="alert"
                                        style={{
                                            marginBottom: '16px',
                                            padding: '12px 14px',
                                            borderRadius: '6px',
                                            backgroundColor: isAccountLocked ? '#fff4e5' : '#fdecea',
                                            border: `1px solid ${isAccountLocked ? '#f5a623' : '#f5c2c7'}`,
                                            color: isAccountLocked ? '#8a5a00' : '#842029',
                                            fontSize: '14px',
                                            lineHeight: 1.45,
                                        }}
                                    >
                                        <strong style={{ display: 'block', marginBottom: '4px' }}>
                                            {isAccountLocked ? 'Account locked' : 'Sign in failed'}
                                        </strong>
                                        <span>{loginError}</span>
                                        {isAccountLocked && (
                                            <span style={{ display: 'block', marginTop: '6px' }}>
                                                {lockedUntilLabel
                                                    ? `You can try again after ${lockedUntilLabel}, or contact an administrator to unlock your account.`
                                                    : 'Please try again later, or contact an administrator to unlock your account.'}
                                            </span>
                                        )}
                                    </div>
                                )}
                                <form className='gb-login-form' onSubmit={handleSubmit}>
                                    {renderInputField('email', 'Email Address', 'email', "", '')}
                                    {renderInputField('password', 'Password', 'password', iconHide, '')}
                                    <div className='gb-field-wrap'>
                                        <Link to={'/forget-password'} className='forget-link'>Forgot Password?</Link>
                                    </div>
                                    <div className='gb-form-footer'>
                                        <button type="submit" className='form-submit' disabled={loading}>
                                            {loading ? 'Signing In...' : 'Sign In'}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                        <div className='landingCol'>
                            <div className='imageWrapper'>
                                <img width={"300px"} height={"300px"} src={GamingBoardBahamasLogo} className="booksAnimation" alt="" />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default Login;
